import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Layout from '../components/Layout';

const EMPTY_FORM = {
  group_id: '', car_id: '', from_date: '', to_date: '',
  promotion: '', promotion_type: '2', // default: Decrease
};

export default function PromotionalPrice() {
  const navigate = useNavigate();

  // ── Dropdowns ──
  const [groups,   setGroups]   = useState([]);
  const [cars,     setCars]     = useState([]);

  // ── Table ──
  const [data,       setData]       = useState([]);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total,      setTotal]      = useState(0);
  const [filterGroup, setFilterGroup] = useState(0);
  const [loading,    setLoading]    = useState(false);

  // ── Form ──
  const [form,    setForm]    = useState(EMPTY_FORM);
  const [editId,  setEditId]  = useState(null);
  const [saving,  setSaving]  = useState(false);

  // ── Auth ──
  useEffect(() => {
    if (!localStorage.getItem('token')) { navigate('/login'); return; }
  }, []);

  // ── Load groups ──
  useEffect(() => {
    API.get('/cargroup', { params: { page: 1, limit: 100 } })
      .then(r => setGroups(r.data.data || []))
      .catch(() => {});
  }, []);

  // ── Load cars when group changes ──
  useEffect(() => {
    if (!form.group_id) { setCars([]); return; }
    API.get(`/promotional-price/cars-by-group/${form.group_id}`)
      .then(r => setCars(r.data || []))
      .catch(() => setCars([]));
  }, [form.group_id]);

  // ── Fetch table data ──
  const fetchData = useCallback((pg = page, gid = filterGroup) => {
    setLoading(true);
    API.get('/promotional-price', { params: { page: pg, limit: 10, group_id: gid } })
      .then(r => {
        setData(r.data.data);
        setTotalPages(r.data.totalPages);
        setTotal(r.data.total);
      })
      .catch(() => toast.error('Failed to load records'))
      .finally(() => setLoading(false));
  }, [page, filterGroup]);

  useEffect(() => { fetchData(page, filterGroup); }, [page]);

  // ── Form change ──
  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => {
      const updated = { ...f, [name]: value };
      if (name === 'group_id') updated.car_id = ''; // reset car on group change
      return updated;
    });
  };

  // ── Edit ──
  const handleEdit = (row) => {
    setEditId(row.promotional_id);
    setForm({
      group_id:       row.group_id,
      car_id:         row.car_id,
      from_date:      row.promotion_frmdt?.slice(0, 10) || '',
      to_date:        row.promotion_todt?.slice(0, 10)  || '',
      promotion:      row.promotion,
      promotion_type: String(row.promotion_type),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancel = () => { setForm(EMPTY_FORM); setEditId(null); };

  // ── Submit ──
  const handleSubmit = async () => {
    if (!form.group_id)      { toast.warning('Select a car group');         return; }
    if (!form.car_id)        { toast.warning('Select a car');               return; }
    if (!form.from_date)     { toast.warning('Select From Date');           return; }
    if (!form.to_date)       { toast.warning('Select To Date');             return; }
    if (form.from_date > form.to_date) { toast.warning('From Date must be before To Date'); return; }
    if (!form.promotion || isNaN(form.promotion) || form.promotion <= 0 || form.promotion > 100) {
      toast.warning('Enter a valid promotion % (1–100)'); return;
    }

    setSaving(true);
    try {
      const payload = {
        group_id:       parseInt(form.group_id),
        car_id:         parseInt(form.car_id),
        from_date:      form.from_date,
        to_date:        form.to_date,
        promotion:      parseFloat(form.promotion),
        promotion_type: form.promotion_type,
      };

      if (editId) {
        await API.put(`/promotional-price/${editId}`, payload);
        toast.success('Promotional price updated');
      } else {
        await API.post('/promotional-price', payload);
        toast.success('Promotional price added');
      }

      setForm(EMPTY_FORM); setEditId(null);
      fetchData(page, filterGroup);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Operation failed');
    }
    setSaving(false);
  };

  // ── Delete ──
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this promotional price?')) return;
    try {
      await API.delete(`/promotional-price/${id}`);
      toast.success('Deleted successfully');
      fetchData(page, filterGroup);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  const fmtDate = d => d
    ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  return (
    <Layout activePage="Promotional Price">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Page header */}
      <div style={S.header}>
        <div>
          <h2 style={S.title}>Promotional Price</h2>
          <p style={S.subtitle}>Manage price increases and decreases by date range</p>
        </div>
      </div>

      {/* ── Form Card ── */}
      <div style={S.card}>
        <div style={S.cardHead}>
          <span style={S.cardHeadTitle}>
            {editId ? '✏️ Edit Promotional Price' : '➕ Add Promotional Price'}
          </span>
          {editId && <span style={S.editingBadge}>Editing #{editId}</span>}
        </div>

        {/* Row 1: Group + Car + Type */}
        <div style={S.formGrid3}>
          <div style={S.fg}>
            <label style={S.label}>Car Group <span style={S.req}>*</span></label>
            <select name="group_id" value={form.group_id} onChange={handleChange} style={S.select}>
              <option value="">— Select Group —</option>
              {groups.map(g => (
                <option key={g.group_id} value={g.group_id}>{g.group_name}</option>
              ))}
            </select>
          </div>

          <div style={S.fg}>
            <label style={S.label}>Car <span style={S.req}>*</span></label>
            <select name="car_id" value={form.car_id} onChange={handleChange} style={S.select}
              disabled={!form.group_id}>
              <option value="">— Select Car —</option>
              {cars.map(c => (
                <option key={c.car_id} value={c.car_id}>{c.car_name}</option>
              ))}
            </select>
          </div>

          <div style={S.fg}>
            <label style={S.label}>Promotion Type <span style={S.req}>*</span></label>
            <select name="promotion_type" value={form.promotion_type} onChange={handleChange} style={S.select}>
              <option value="1">📈 Increase</option>
              <option value="2">📉 Decrease</option>
            </select>
          </div>
        </div>

        {/* Row 2: From Date + To Date + % + Buttons */}
        <div style={{ ...S.formGrid3, gridTemplateColumns: '1fr 1fr 1fr auto', marginTop: 14 }}>
          <div style={S.fg}>
            <label style={S.label}>From Date <span style={S.req}>*</span></label>
            <input type="date" name="from_date" value={form.from_date}
              onChange={handleChange} style={S.input} />
          </div>

          <div style={S.fg}>
            <label style={S.label}>To Date <span style={S.req}>*</span></label>
            <input type="date" name="to_date" value={form.to_date}
              min={form.from_date || undefined}
              onChange={handleChange} style={S.input} />
          </div>

          <div style={S.fg}>
            <label style={S.label}>Promotion % <span style={S.req}>*</span></label>
            <div style={{ position: 'relative' }}>
              <input type="number" name="promotion" value={form.promotion}
                min={0.1} max={100} step={0.1}
                placeholder="e.g. 10"
                onChange={handleChange}
                style={{ ...S.input, paddingRight: 36 }} />
              <span style={S.pctSign}>%</span>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
            {editId && (
              <button style={S.cancelBtn} onClick={handleCancel}>Cancel</button>
            )}
            <button style={S.submitBtn} onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving…' : editId ? 'Update' : 'Add'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Table Card ── */}
      <div style={{ ...S.card, marginTop: 20 }}>

        {/* Table header + filter */}
        <div style={S.tableTopRow}>
          <span style={S.cardHeadTitle}>Manage Promotional Prices</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: 12, color: '#999' }}>Filter by Group:</label>
            <select
              value={filterGroup}
              onChange={e => { const v = parseInt(e.target.value); setFilterGroup(v); setPage(1); fetchData(1, v); }}
              style={{ ...S.select, width: 200, padding: '7px 12px' }}
            >
              <option value={0}>— All Groups —</option>
              {groups.map(g => (
                <option key={g.group_id} value={g.group_id}>{g.group_name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr style={S.theadRow}>
                <th style={S.th}>#</th>
                <th style={S.th}>Group Name</th>
                <th style={S.th}>Car Name</th>
                <th style={S.th}>From Date</th>
                <th style={S.th}>To Date</th>
                <th style={S.th}>Promo %</th>
                <th style={S.th}>Type</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={S.emptyCell}>Loading…</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={8} style={S.emptyCell}>No records available</td></tr>
              ) : data.map((row, i) => {
                const isIncrease = String(row.promotion_type) === '1';
                const isEditing  = editId === row.promotional_id;
                return (
                  <tr key={row.promotional_id} style={{
                    ...S.tr,
                    background: isEditing ? '#f3f2ff' : 'transparent'
                  }}>
                    <td style={S.td}>
                      <span style={S.serial}>{(page - 1) * 10 + i + 1}</span>
                    </td>
                    <td style={S.td}>
                      <span style={S.groupChip}>{row.group_name}</span>
                    </td>
                    <td style={S.td}>
                      <span style={S.nameChip}>{row.car_name}</span>
                    </td>
                    <td style={S.td}>
                      <span style={S.dateChip}>{fmtDate(row.promotion_frmdt)}</span>
                    </td>
                    <td style={S.td}>
                      <span style={{ ...S.dateChip, background: '#fff8f0', color: '#e07b00' }}>
                        {fmtDate(row.promotion_todt)}
                      </span>
                    </td>
                    <td style={S.td}>
                      <span style={{
                        ...S.pctChip,
                        background: isIncrease ? '#fff0f0' : '#f0fff6',
                        color:      isIncrease ? '#c0392b' : '#1a9e5c',
                      }}>
                        {isIncrease ? '+' : '-'}{row.promotion}%
                      </span>
                    </td>
                    <td style={S.td}>
                      <span style={{
                        ...S.typeBadge,
                        background: isIncrease ? '#fff0f0' : '#f0fff6',
                        color:      isIncrease ? '#c0392b' : '#1a9e5c',
                        border:     `1px solid ${isIncrease ? '#ffd0d0' : '#b8f0d0'}`,
                      }}>
                        {isIncrease ? '📈 Increase' : '📉 Decrease'}
                      </span>
                    </td>
                    <td style={{ ...S.td, textAlign: 'right' }}>
                      <button style={S.editBtn} onClick={() => handleEdit(row)}>Edit</button>
                      <button style={S.delBtn}  onClick={() => handleDelete(row.promotional_id)}>Delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={S.pagination}>
          <span style={S.paginInfo}>
            {total > 0
              ? `Showing ${(page - 1) * 10 + 1}–${Math.min(page * 10, total)} of ${total}`
              : ''}
          </span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button style={S.pgBtn} disabled={page === 1}
              onClick={() => setPage(p => p - 1)}>← Prev</button>
            <span style={S.paginInfo}>Page {page} / {totalPages || 1}</span>
            <button style={S.pgBtn} disabled={page === totalPages || totalPages === 0}
              onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

/* ─── Styles ─── */
const S = {
  header:   { marginBottom: 20 },
  title:    { margin: 0, fontSize: 26, fontWeight: 800, color: '#1a1d2e', letterSpacing: '-0.5px' },
  subtitle: { margin: '4px 0 0', fontSize: 13, color: '#999' },

  card: {
    background: '#fff', borderRadius: 16,
    boxShadow: '0 2px 16px rgba(0,0,0,0.06)', overflow: 'hidden'
  },
  cardHead: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 20px', borderBottom: '1px solid #f0f0f0'
  },
  cardHeadTitle: { fontSize: 14, fontWeight: 700, color: '#1a1d2e' },
  editingBadge: {
    fontSize: 11, fontWeight: 700, background: '#f3f2ff',
    color: '#6C63FF', borderRadius: 20, padding: '3px 10px'
  },

  formGrid3: {
    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
    gap: 16, padding: '20px'
  },
  fg:    { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 12, fontWeight: 600, color: '#555' },
  req:   { color: '#ff6b6b' },
  select: {
    padding: '9px 14px', border: '1.5px solid #e0e0e0', borderRadius: 10,
    fontSize: 13, fontFamily: 'inherit', color: '#333', outline: 'none',
    background: '#fff', width: '100%', cursor: 'pointer'
  },
  input: {
    padding: '9px 14px', border: '1.5px solid #e0e0e0', borderRadius: 10,
    fontSize: 13, fontFamily: 'inherit', color: '#333', outline: 'none',
    width: '100%', boxSizing: 'border-box'
  },
  pctSign: {
    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
    fontSize: 13, color: '#aaa', fontWeight: 700, pointerEvents: 'none'
  },
  cancelBtn: {
    padding: '9px 18px', background: '#f5f5f5', color: '#666',
    border: 'none', borderRadius: 10, cursor: 'pointer',
    fontSize: 13, fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap'
  },
  submitBtn: {
    padding: '9px 22px', background: 'linear-gradient(135deg,#6C63FF,#43BCCD)',
    color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer',
    fontSize: 13, fontWeight: 700, fontFamily: 'inherit', whiteSpace: 'nowrap'
  },

  tableTopRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 20px', borderBottom: '1px solid #f0f0f0', flexWrap: 'wrap', gap: 12
  },

  table:    { width: '100%', borderCollapse: 'collapse' },
  theadRow: { background: '#fafafa' },
  th: {
    padding: '12px 16px', fontSize: 11, fontWeight: 700, color: '#999',
    textAlign: 'left', borderBottom: '2px solid #f0f0f0',
    textTransform: 'uppercase', letterSpacing: '0.5px'
  },
  tr:       { transition: 'background 0.12s' },
  td:       { padding: '13px 16px', fontSize: 13, color: '#333', borderBottom: '1px solid #f8f8f8' },
  emptyCell:{ textAlign: 'center', padding: 40, color: '#bbb', fontSize: 14 },

  serial:    { background: '#f3f2ff', color: '#6C63FF',  borderRadius: 20, padding: '3px 10px',  fontSize: 12, fontWeight: 700 },
  groupChip: { background: '#f0f8ff', color: '#1a6eb5',  borderRadius: 20, padding: '4px 12px',  fontSize: 12, fontWeight: 600 },
  nameChip:  { background: '#f0fff6', color: '#1a9e5c',  borderRadius: 20, padding: '4px 12px',  fontSize: 12, fontWeight: 600 },
  dateChip:  { background: '#f0f8ff', color: '#1a6eb5',  borderRadius: 20, padding: '4px 12px',  fontSize: 12, fontWeight: 600 },
  pctChip:   { borderRadius: 20, padding: '4px 12px', fontSize: 13, fontWeight: 700 },
  typeBadge: { borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 600 },

  editBtn: {
    marginRight: 6, background: '#f3f2ff', color: '#6C63FF',
    border: '1px solid #d4d0ff', padding: '5px 14px',
    borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit'
  },
  delBtn: {
    background: '#fff2f5', color: '#FF6584',
    border: '1px solid #ffd0d8', padding: '5px 14px',
    borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit'
  },

  pagination: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 20px', borderTop: '1px solid #f0f0f0'
  },
  paginInfo: { fontSize: 12, color: '#999' },
  pgBtn: {
    padding: '5px 14px', background: '#f3f2ff', color: '#6C63FF',
    border: '1px solid #d4d0ff', borderRadius: 8, cursor: 'pointer',
    fontSize: 12, fontWeight: 600, fontFamily: 'inherit'
  },
};
